import type { MiSolicitud } from "@/lib/types";
import {
  etiquetaTipoSolicitud,
  etiquetaSubtipoSolicitud,
} from "@/lib/solicitud-labels";
import { etiquetaEstado } from "@/lib/estado-labels";
import { formatFechaIso, formatFechaHoraIso } from "@/shared/format/fecha";

export type SolicitudSortKey =
  | "folio"
  | "estado"
  | "tipo"
  | "organizacion"
  | "area"
  | "proveedor"
  | "rfcProveedor"
  | "descripcion"
  | "total"
  | "moneda"
  | "fechaPago"
  | "numDoc"
  | "creacion"
  | "ultimaActividad";

export type SortDir = "asc" | "desc";

export interface SolicitudSort {
  key: SolicitudSortKey;
  dir: SortDir;
}

/** Comparación ascendente por la columna dada. */
function comparar(a: MiSolicitud, b: MiSolicitud, key: SolicitudSortKey): number {
  switch (key) {
    case "folio":
      return a.numeroSolicitud - b.numeroSolicitud;
    case "estado":
      return (a.estado ?? "").localeCompare(b.estado ?? "", "es");
    case "tipo":
      return (
        (a.tipo ?? "").localeCompare(b.tipo ?? "", "es") ||
        (a.subtipo ?? "").localeCompare(b.subtipo ?? "", "es")
      );
    case "organizacion":
      return (a.organizacionNombre ?? "").localeCompare(b.organizacionNombre ?? "", "es");
    case "area":
      return (a.areaNombre ?? "").localeCompare(b.areaNombre ?? "", "es");
    case "proveedor":
      return (a.proveedorRazonSocial ?? "").localeCompare(b.proveedorRazonSocial ?? "", "es");
    case "rfcProveedor":
      return (a.proveedorRfc ?? "").localeCompare(b.proveedorRfc ?? "", "es");
    case "descripcion":
      return (a.descripcionPrimerConcepto ?? "").localeCompare(b.descripcionPrimerConcepto ?? "", "es");
    case "total":
      return (a.totalCfdi ?? Number.NEGATIVE_INFINITY) - (b.totalCfdi ?? Number.NEGATIVE_INFINITY);
    case "moneda":
      return (a.monedaCfdi ?? "").localeCompare(b.monedaCfdi ?? "", "es");
    case "fechaPago":
      return (a.fechaPago ?? "").localeCompare(b.fechaPago ?? "", "es");
    // HOTSPOT-DOMINIO: número de documento · aún sin dato · orden no-op.
    case "numDoc":
      return 0;
    case "creacion":
      return new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime();
    case "ultimaActividad":
      return new Date(a.fechaUltimoEvento).getTime() - new Date(b.fechaUltimoEvento).getTime();
    default:
      return 0;
  }
}

/** Devuelve una copia ordenada · si `sort` es null conserva el orden original. */
export function ordenarSolicitudes(
  items: MiSolicitud[],
  sort: SolicitudSort | null,
): MiSolicitud[] {
  if (!sort) return items;
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => factor * comparar(a, b, sort.key));
}

/** Estado siguiente al hacer click en una columna: nueva → asc; misma → alterna asc/desc. */
export function siguienteSort(actual: SolicitudSort | null, key: SolicitudSortKey): SolicitudSort {
  if (actual?.key === key) {
    return { key, dir: actual.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}

// --- Filtro por columna ------------------------------------------------------

export type SolicitudFiltros = Partial<Record<SolicitudSortKey, string>>;

/** Texto buscable de una celda para el filtro por columna (contains normalizado). */
export function textoColumna(it: MiSolicitud, key: SolicitudSortKey): string {
  switch (key) {
    case "folio":
      return it.folio ?? "";
    case "estado":
      return etiquetaEstado(it.estado, {
        nivelActual: it.flujoNivelActual,
        rolActual: it.flujoRolActual,
      });
    case "tipo":
      return `${etiquetaTipoSolicitud(it.tipo)} ${etiquetaSubtipoSolicitud(it.subtipo)}`;
    case "organizacion":
      return it.organizacionNombre ?? "";
    case "area":
      return it.areaNombre ?? "";
    case "proveedor":
      return it.proveedorRazonSocial ?? "";
    case "rfcProveedor":
      return it.proveedorRfc ?? "";
    case "descripcion":
      return it.descripcionPrimerConcepto ?? "";
    case "total":
      // Número con 2 decimales sin separadores (consistente con el server: ToString("0.00")).
      return it.totalCfdi !== null ? it.totalCfdi.toFixed(2) : "";
    case "moneda":
      return it.monedaCfdi ?? "";
    case "fechaPago":
      return it.fechaPago ?? "";
    // HOTSPOT-DOMINIO: número de documento · aún no hay dato en MiSolicitud.
    case "numDoc":
      return "";
    case "creacion":
      return formatFechaIso(it.fechaInicio);
    case "ultimaActividad":
      return formatFechaHoraIso(it.fechaUltimoEvento);
    default:
      return "";
  }
}

/** Normaliza para un contains robusto: minúsculas + sin acentos + solo alfanuméricos.
 *  Debe coincidir con la normalización del backend (ListarMisSolicitudesQuery.Normalizar). */
export function normalizarBusqueda(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]/g, "");
}

/** Filtra por todas las columnas con texto (AND, contains normalizado). */
export function filtrarSolicitudes(
  items: MiSolicitud[],
  filtros: SolicitudFiltros,
): MiSolicitud[] {
  const activos = Object.entries(filtros)
    .map(([k, v]) => [k as SolicitudSortKey, normalizarBusqueda(v ?? "")] as const)
    .filter(([, v]) => v.length > 0);
  if (activos.length === 0) return items;
  return items.filter((it) =>
    activos.every(([key, texto]) => normalizarBusqueda(textoColumna(it, key)).includes(texto)),
  );
}

/** Filtros con al menos un valor no vacío → objeto listo para query params (server-side). */
export function filtrosNoVacios(filtros: SolicitudFiltros): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filtros)) {
    if (v && v.trim().length > 0) out[k] = v.trim();
  }
  return out;
}
